<template>
    <CreateFolderComponent
        :open="showCreateFolderForm"
        @close="showCreateFolderForm = false"
        :gallery-slug="gallerySlug"
    />

    <div class="sticky top-20 flex flex-col gap-3">
        <!-- Scenes block -->
        <div class="bg-white rounded-md border border-gray-200 p-4 flex flex-col">
            <div class="flex items-start justify-between mb-4">
                <div class="min-w-0">
                    <h2 class="text-base font-semibold text-gray-900 mb-1">{{ $t('folders.title') }}</h2>
                    <p class="text-[11px] text-gray-500 leading-relaxed pr-2">{{ $t('folders.tip_text') }}</p>
                </div>
                <button
                    v-if="folders.length > compactThreshold"
                    type="button"
                    @click.prevent="showCreateFolderForm = true"
                    class="p-1.5 bg-gray-50 text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors shrink-0 cursor-pointer"
                    :title="$t('folders.create_folder')"
                >
                    <PlusIcon class="w-4 h-4" />
                </button>
            </div>

            <!-- All files -->
            <div class="mb-2 shrink-0">
                <a
                    href="#"
                    @click.prevent="selectFolder(null)"
                    :class="[
                        'w-full flex items-center justify-between px-2.5 py-1.5 text-sm font-medium border rounded-md transition-colors group',
                        selectedFolder === null
                            ? 'bg-gray-100 border-gray-200 text-gray-900'
                            : 'bg-white hover:bg-gray-50 border-gray-200 text-gray-600 hover:text-gray-900'
                    ]"
                >
                    <div class="flex items-center gap-2 min-w-0">
                        <Squares2X2Icon :class="[
                            'w-4 h-4 shrink-0 transition-colors',
                            selectedFolder === null ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500'
                        ]" />
                        <span class="truncate">{{ $t('files.all_files') }}</span>
                    </div>
                    <span :class="[
                        'text-xs font-semibold shrink-0 transition-colors',
                        selectedFolder === null ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500'
                    ]">{{ gallery.photos_count }}</span>
                </a>
            </div>

            <!-- Scenes list (draggable) -->
            <draggable
                v-model="sortedFolders"
                @start="drag=true"
                @end="drag=false"
                item-key="id"
                tag="nav"
                class="space-y-0.5 overflow-y-auto pr-1 -mr-1 flex-1 pb-1 max-h-[60vh]"
                drag-class="sortable-selected-moving"
            >
                <template #item="{element}">
                    <a
                        href="#"
                        @click.prevent="selectFolder(element)"
                        @contextmenu.prevent="showContextMenuForFolder(element, $event)"
                        :class="[
                            'w-full flex items-center justify-between px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors group',
                            (selectedFolder && selectedFolder.id === element.id)
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        ]"
                    >
                        <div class="flex items-center gap-2 min-w-0">
                            <span class="w-1.5 h-1.5 rounded-full shrink-0" :style="{backgroundColor: element.color}"></span>
                            <span class="truncate">{{ element.title }}</span>
                            <span v-if="element.is_hidden || element.is_hidden_by_client" class="shrink-0">🫣</span>
                        </div>
                        <span :class="[
                            'text-xs font-medium shrink-0',
                            (selectedFolder && selectedFolder.id === element.id) ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-500'
                        ]">{{ element.photos_count }}</span>
                    </a>
                </template>
            </draggable>

            <!-- Prominent create button for small lists -->
            <button
                v-if="folders.length <= compactThreshold"
                type="button"
                @click.prevent="showCreateFolderForm = true"
                class="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-royal-blue-200 hover:bg-royal-blue-50 hover:border-royal-blue-300 text-royal-blue-700 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0"
            >
                <PlusIcon class="w-3.5 h-3.5" />
                {{ $t('folders.create_folder') }}
            </button>
        </div>

        <!-- Trash button (standalone) -->
        <a
            v-if="gallery.deleted_count > 0"
            href="#"
            @click.prevent="selectTrashFolder()"
            :class="[
                'w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium rounded-md border transition-colors group shrink-0',
                selectedFolder?.slug == 'trash'
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:text-red-600 hover:bg-red-50 hover:border-red-200'
            ]"
        >
            <div class="flex items-center gap-2.5">
                <TrashIcon :class="[
                    'w-4 h-4 transition-colors',
                    selectedFolder?.slug == 'trash' ? 'text-red-500' : 'text-gray-400 group-hover:text-red-500'
                ]" />
                <span>{{ $t('files.trashed_files') }}</span>
            </div>
            <span :class="[
                'text-xs font-semibold transition-colors',
                selectedFolder?.slug == 'trash' ? 'text-red-500' : 'text-gray-400 group-hover:text-red-500'
            ]">{{ gallery.deleted_count }}</span>
        </a>
    </div>

    <FolderContextMenuComponent
        v-if="contextMenuFolder"
        :folder="contextMenuFolder as any"
        :is-visible="showContextMenu"
        :position="contextMenuPosition"
        @close="closeContextMenu"
        @edit="handleEditFolder"
        @delete="handleDeleteFolder"
        @copy-link="handleCopyLink"
    />

    <DeleteFolderConfirmationModalComponent
        :open="isDeletingConfirmation !== null"
        @close="isDeletingConfirmation = null"
        @confirm-action="confirmDeleteFolder"
        :title="$t('folders.delete_confirmation_title')"
        :message="$t('folders.delete_confirmation_message')"
        :second-message="$t('folders.delete_confirmation_second_message')"
        :button-text="$t('folders.delete_confirmation_button')"
    />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";
import draggable from 'vuedraggable'

import {Folder} from "@/types/GalleryType";
import CreateFolderComponent from "@/components/Galleries/Folders/CreateFolderComponent.vue";
import FolderContextMenuComponent from "@/components/Galleries/Folders/FolderContextMenuComponent.vue";
import DeleteFolderConfirmationModalComponent from "@/components/Galleries/Folders/DeleteFolderConfirmationModalComponent.vue";
import {deleteFolder} from "@/data/requests/gallery";
import {PlusIcon, Squares2X2Icon, TrashIcon} from "@heroicons/vue/20/solid";

export default defineComponent({
    name: "FoldersListComponent",

    components: {
        CreateFolderComponent,
        FolderContextMenuComponent,
        DeleteFolderConfirmationModalComponent,
        draggable,
        PlusIcon,
        Squares2X2Icon,
        TrashIcon,
    },

    emits: ['select-folder', 'edit-folder'],

    props: [
        'selectedFolder',
        'gallerySlug',
    ],

    data() {
        return {
            drag: false,

            showCreateFolderForm: false,

            compactThreshold: 3,

            // Context menu
            showContextMenu: false,
            contextMenuFolder: null as any,
            contextMenuPosition: { x: 0, y: 0 },

            // Delete confirmation
            isDeletingConfirmation: null as any,
        };
    },

    computed: {
        ...mapGetters({
            folders: 'galleries/folders',
            gallery: 'galleries/gallery',
        }),

        sortedFolders: {
            get(): Folder[] {
                return this.folders;
            },
            set(value: Folder[]) {
                this.sortFolders({
                    gallery: this.gallerySlug,
                    folders: value,
                });
            }
        },
    },

    methods: {
        ...mapActions({
            sortFolders: "galleries/sortFolders",
            loadFolders: "galleries/loadFolders",
            successAlert: "alerts/success",
            errorAlert: "alerts/error",
        }),

        selectFolder(folder: Folder | null) {
            this.$emit('select-folder', folder);
        },

        selectTrashFolder() {
            this.$emit('select-folder', { slug: 'trash' } as any);
        },

        // Context menu methods
        showContextMenuForFolder(folder: Folder, event: MouseEvent) {
            this.contextMenuFolder = folder;
            this.contextMenuPosition = {
                x: event.clientX,
                y: event.clientY
            };
            this.showContextMenu = true;
        },

        closeContextMenu() {
            this.showContextMenu = false;
            this.contextMenuFolder = null;
        },

        handleEditFolder(folder: Folder) {
            this.$emit('edit-folder', folder);
            this.closeContextMenu();
        },

        handleDeleteFolder(folder: Folder) {
            this.isDeletingConfirmation = folder;
            this.closeContextMenu();
        },

        confirmDeleteFolder() {
            if (this.isDeletingConfirmation) {
                deleteFolder(this.gallerySlug, this.isDeletingConfirmation.slug, () => {
                    this.successAlert({ message: this.$t('folders.folder_deleted_success') });
                    this.isDeletingConfirmation = null;
                    this.loadFolders({ gallery: this.gallerySlug });
                    this.selectFolder(null)
                }, (type, data) => {
                    this.errorAlert({
                        message: data?.message || this.$t('folders.folder_delete_error')
                    });
                })
            }
        },

        handleCopyLink(folder: Folder) {
            navigator.clipboard.writeText(folder.public_url).then(() => {
                this.successAlert({ message: this.$t('folders.link_copied_success') });
                this.closeContextMenu();
            }).catch(() => {
                this.errorAlert({ message: this.$t('folders.link_copy_error') });
            });
        },

    }
})
</script>

<style scoped>

</style>